import { gql, useApolloClient, useQuery } from "@apollo/client";
import { useParams } from "react-router-dom";
import PageHeader from "../theme/components/common/PageHeader";
import * as React from "react";
import Table from "react-bootstrap/Table";
import Card from "react-bootstrap/Card";
import Form from "react-bootstrap/Form";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  GetMeasurementStationConversions,
  GetMeasurementStationConversionsVariables,
  GetMeasurementStationConversions_channel as Channel,
} from "./__generated__/GetMeasurementStationConversions";
import { GetUnits_unit } from "../utils/__generated__/GetUnits";
import apiClient from "../api";
import { toast } from "react-toastify";
import { useAuth } from "../state/auth";
import Button from "react-bootstrap/Button";
import BackToLink from "../components/common/BackToLink";
import FormattedFormControl from "components/form/FormattedFormControl";
import SessionTimeout from "components/common/SessionTimeout";
import { useSortedUnits } from "../hooks/units";
import { gettext } from "../i18n";
import CreatableSelect from "react-select/creatable";
import "./ConversionTablePage.scss";
import classNames from "classnames";

const GET_CONVERSIONS = gql`
  query GetMeasurementStationConversions($identifier: String) {
    channel(where: { station: { identifier: { _eq: $identifier } } }) {
      id
      identifier
      name
      system_name
      unit {
        id
        name
        symbol
      }
      conversion {
        id
        type
        raw_max
        raw_min
        engineering_max
        engineering_min
        offset
      }
    }
  }
`;

const GET_CHANNEL_SUGGESTIONS = gql`
  query GetChannelSuggestions {
    channel_name_preset(order_by: { name: asc }) {
      id
      name
      unit_symbol
    }
    channel(where: { name: { _is_null: false, _neq: "" } }) {
      identifier
      name
      unit {
        id
      }
      conversion {
        type
        raw_min
        raw_max
        engineering_min
        engineering_max
        offset
      }
    }
  }
`;

interface ChannelSuggestion {
  name: string;
  unit_id: number | null;
  type: string;
  raw_min: number | null;
  raw_max: number | null;
  engineering_min: number | null;
  engineering_max: number | null;
  offset: number | null;
}



const ConversionTablePage = () => {
  const { identifier } = useParams();
  const { data, error } = useQuery<
    GetMeasurementStationConversions,
    GetMeasurementStationConversionsVariables
  >(GET_CONVERSIONS, {
    variables: {
      identifier,
    },
  });
  const { data: sortedUnits, refetch: refetchUnits } = useSortedUnits();
  const [edits, setEdits] = React.useState<Record<number, Record<string, any>>>(
    {}
  );
  const apolloClient = useApolloClient();
  const { user } = useAuth();

  // Hooks must be called before any early returns
  const { data: suggestionsData } = useQuery(GET_CHANNEL_SUGGESTIONS);
  const channelSuggestions = React.useMemo((): Map<string, ChannelSuggestion[]> => {
    const map = new Map<string, ChannelSuggestion[]>();
    for (const ch of (suggestionsData as any)?.channel ?? []) {
      if (!ch.name) continue;
      const existing = map.get(ch.identifier) ?? [];
      if (!existing.find((s: ChannelSuggestion) => s.name === ch.name)) {
        map.set(ch.identifier, [
          ...existing,
          {
            name: ch.name,
            unit_id: ch.unit?.id ?? null,
            type: ch.conversion?.type ?? "none",
            raw_min: ch.conversion?.raw_min ?? null,
            raw_max: ch.conversion?.raw_max ?? null,
            engineering_min: ch.conversion?.engineering_min ?? null,
            engineering_max: ch.conversion?.engineering_max ?? null,
            offset: ch.conversion?.offset ?? null,
          },
        ]);
      }
    }
    return map;
  }, [suggestionsData]);
  const channelNamePresets: { id: number; name: string; unit_symbol: string | null }[] =
    (suggestionsData as any)?.channel_name_preset ?? [];

  if (!data) {
    return <div />;
  }
  if (error) {
    console.error(JSON.stringify(error, null, 2));
    return <SessionTimeout />;
  }
  if (data?.channel.length === 0) {
    return <div>Not found.</div>;
  }

  const channels = data.channel;
  const editValue = (channelId: number, key: string, value: any) => {
    setEdits((prev) => {
      const newEdits = { ...prev };
      if (!newEdits[channelId]) {
        newEdits[channelId] = {};
      }
      newEdits[channelId][key] = value;
      return newEdits;
    });
  };
  const channelsWithEdits = channels.map((c) => {
    const edit = edits[c.id];
    if (!edit) {
      return c;
    }
    const editedChannel: any = {
      ...c,
      conversion: { ...c.conversion },
    };
    for (const [key, value] of Object.entries(edit)) {
      let [namespace, realKey] = key.split(".");
      if (realKey) {
        (editedChannel as any)[namespace][realKey] = value;
      } else {
        (editedChannel as any)[namespace] = value;
      }
    }
    return editedChannel;
  });

  const onSave = (e: any) => {
    e.preventDefault();
    if (!Object.keys(edits).length) {
      return;
    }

    const data = edits;
    apiClient
      .request("/admin/edit-channels", {
        method: "POST",
        data,
      })
      .then(() => {
        apolloClient.resetStore().catch((e) => {
          console.error("reseting apollo store failed, refreshing", e);
          window.location.reload();
        });
        toast.success("Data saved successfully", { theme: "colored" });
        refetchUnits();
      })
      .catch((e) => {
        console.error(e);
        toast.error(`Error saving data: ${e.message}`, { theme: "colored" });
      });
  };

  return (
    <div className="ConversionTablePage">
      <BackToLink to={`/stations/${identifier}`}>
        Back to measurement station.
      </BackToLink>
      <PageHeader
        title={gettext(`Conversion table`)}
        className="mb-3"
      ></PageHeader>
      <Card className="mb-3">
        <Card.Body>
          <div className="table-wrapper">
            <ConversionTable
              channels={channelsWithEdits}
              editValue={editValue}
              units={sortedUnits}
              channelSuggestions={channelSuggestions}
              channelNamePresets={channelNamePresets}
              isAdmin={!!user?.admin}
              onPresetsChanged={() => apolloClient.resetStore()}
            />
          </div>
          <Button type="button" onClick={onSave}>
            Save
          </Button>
        </Card.Body>
      </Card>
    </div>
  );
};

interface ConversionTableProps {
  channels: Channel[];
  editValue: (channelId: number, key: string, value: any) => void;
  units: GetUnits_unit[];
  channelSuggestions: Map<string, ChannelSuggestion[]>;
  channelNamePresets: { id: number; name: string; unit_symbol: string | null }[];
  isAdmin: boolean;
  onPresetsChanged: () => void;
}

const ConversionTable = ({
  channels,
  editValue,
  units,
  channelSuggestions,
  channelNamePresets,
  isAdmin,
  onPresetsChanged,
}: ConversionTableProps) => {
  const [visibleChannels, setVisibleChannels] = React.useState<number[]>([]);
  const sortedChannels = [...channels];
  sortedChannels.sort((a, b) => (a.id < b.id ? -1 : 1));
  return (
    <Table>
      <thead>
        <tr>
          <th>Channel</th>
          <th>Name</th>
          <th>System Name</th>
          <th>Unit</th>
          <th>Conversion</th>
          <th>Raw Min.</th>
          <th>Raw Max.</th>
          <th>Eng. Min.</th>
          <th>Eng. Max.</th>
          <th>Offset</th>
        </tr>
      </thead>
      <tbody>
        {sortedChannels.map((channel) => (
          <tr
            key={channel.id}
            className={classNames("channelLine", {
              hideChannel: !visibleChannels.includes(channel.id),
            })}
          >
            <td
              data-label="Channel"
              onClick={() =>
                visibleChannels.includes(channel.id)
                  ? setVisibleChannels(
                      visibleChannels.filter((c) => c !== channel.id)
                    )
                  : setVisibleChannels([...visibleChannels, channel.id])
              }
            >
              <div className="channelId">
                {channel.identifier}
                <FontAwesomeIcon
                  icon={
                    visibleChannels.includes(channel.id)
                      ? "chevron-up"
                      : "chevron-down"
                  }
                  className="me-1"
                />
              </div>
            </td>

            <td data-label="Name">
              <CreatableSelect
                isClearable
                value={channel.name ? { value: channel.name, label: channel.name } : null}
                options={channelNamePresets.map((p) => ({ value: p.name, label: p.name }))}
                onChange={(option: any) => {
                  const val = option?.value ?? "";
                  editValue(channel.id, "name", val);
                  const match = Array.from(channelSuggestions.values())
                    .flat()
                    .find((s) => s.name === val);
                  const preset = channelNamePresets.find((p) => p.name === val);
                  const unitSymbol = val.toLowerCase().includes("lämpötila") ? "°C" : preset?.unit_symbol ?? null;
                  const unitId = unitSymbol ? (units.find((u) => u.symbol === unitSymbol)?.id ?? "") : "";
                  editValue(channel.id, "unit_id", unitId);
                  if (match) {
                    editValue(channel.id, "conversion.type", match.type);
                    editValue(channel.id, "conversion.raw_min", match.raw_min);
                    editValue(channel.id, "conversion.raw_max", match.raw_max);
                    editValue(channel.id, "conversion.engineering_min", match.engineering_min);
                    editValue(channel.id, "conversion.engineering_max", match.engineering_max);
                    editValue(channel.id, "conversion.offset", match.offset);
                  }
                }}
                onCreateOption={(inputValue: string) => {
                  editValue(channel.id, "name", inputValue);
                  if (isAdmin) {
                    apiClient
                      .request("/organization-admin/create-channel-name-preset", {
                        method: "POST",
                        data: { name: inputValue, unitSymbol: null },
                      })
                      .then(() => {
                        toast.success(gettext("Preset saved"), { theme: "colored" });
                        onPresetsChanged();
                      })
                      .catch((e: any) => {
                        toast.error(`${gettext("Error")}: ${e.message}`, { theme: "colored" });
                      });
                  }
                }}
                styles={{
                  control: (base: any) => ({
                    ...base,
                    minHeight: "31px",
                    height: "31px",
                    fontSize: "0.9rem",
                  }),
                  valueContainer: (base: any) => ({
                    ...base,
                    padding: "0 6px",
                  }),
                  indicatorsContainer: (base: any) => ({
                    ...base,
                    height: "31px",
                  }),
                }}
              />
              {isAdmin &&
                channel.name &&
                channelNamePresets.find((p) => p.name === channel.name) && (
                  <button
                    type="button"
                    title={gettext("Delete preset")}
                    className="btn btn-sm btn-link text-danger p-0 mt-1"
                    onClick={() => {
                      const preset = channelNamePresets.find((p) => p.name === channel.name);
                      if (!preset) return;
                      if (window.confirm(`${gettext("Delete preset")} "${preset.name}"?`)) {
                        apiClient
                          .request("/organization-admin/delete-channel-name-preset", {
                            method: "POST",
                            data: { presetId: preset.id },
                          })
                          .then(() => {
                            toast.success(`${gettext("Preset deleted")}: "${preset.name}"`, { theme: "colored" });
                            onPresetsChanged();
                          })
                          .catch((e: any) => {
                            toast.error(`${gettext("Error")}: ${e.message}`, { theme: "colored" });
                          });
                      }
                    }}
                  >
                    {gettext("Delete")}
                  </button>
                )}
            </td>

            <td data-label="System Name">
              <FormattedFormControl
                type="nullableString"
                value={channel.system_name}
                onChange={(e) => {
                  editValue(channel.id, "system_name", e.target.value);
                }}
              />
            </td>
            <td data-label="Unit">
              <Form.Select
                required
                aria-label="Select a unit for the conversion"
                onChange={(e) => {
                  const unitId: number = Number(e.target.value);
                  editValue(channel.id, "unit_id", unitId);
                }}
                value={(channel as any).unit_id ?? channel.unit?.id}
              >
                <option value="">(unassigned)</option>
                {units.map((unit: GetUnits_unit) => (
                  <option key={`unit_${unit.id}`} value={unit.id}>
                    {unit.symbol || gettext("(no unit)")}
                  </option>
                ))}
              </Form.Select>
            </td>
            <td data-label="Conversion">
              <Form.Select
                value={channel.conversion?.type || "none"}
                onChange={(e) => {
                  editValue(channel.id, "conversion.type", e.target.value);
                }}
              >
                <option>none</option>
                <option>raw_value_to_engineering_units</option>
                <option>little_endian_float</option>
              </Form.Select>
            </td>
            <td data-label="Raw Min.">
              <FormattedFormControl
                type="number"
                value={channel.conversion?.raw_min}
                onChange={(e) => {
                  editValue(channel.id, "conversion.raw_min", e.target.value);
                }}
              />
            </td>
            <td data-label="Raw Max.">
              <FormattedFormControl
                type="number"
                value={channel.conversion?.raw_max}
                onChange={(e) => {
                  editValue(channel.id, "conversion.raw_max", e.target.value);
                }}
              />
            </td>
            <td data-label="Eng. Min.">
              <FormattedFormControl
                type="number"
                value={channel.conversion?.engineering_min}
                onChange={(e) => {
                  editValue(
                    channel.id,
                    "conversion.engineering_min",
                    e.target.value
                  );
                }}
              />
            </td>
            <td data-label="Eng. Max.">
              <FormattedFormControl
                type="number"
                value={channel.conversion?.engineering_max}
                onChange={(e) => {
                  editValue(
                    channel.id,
                    "conversion.engineering_max",
                    e.target.value
                  );
                }}
              />
            </td>
            <td data-label="Offset">
              <FormattedFormControl
                type="number"
                value={channel.conversion?.offset}
                onChange={(e) => {
                  editValue(channel.id, "conversion.offset", e.target.value);
                }}
              />
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
};
export default ConversionTablePage;
